import { Controller } from "@hotwired/stimulus"

// Handles the health-system-level "select all locations" checkbox in the
// Add Provider drawer.
//
// Collapsed state: no location checkboxes exist in the DOM yet.
//   Checking the HS checkbox fetches the drilldown with select_all=true,
//   which renders all location checkboxes pre-checked.
//
// Expanded state: location checkboxes are present.
//   Checking/unchecking the HS checkbox toggles all non-disabled location
//   checkboxes in place without a round-trip.
//   Individual location changes keep the HS checkbox in sync
//   (checked / unchecked / indeterminate).
//
// Selection state is tracked by the add-provider-counter controller via
// the selectionChanged data-action on each location checkbox. This controller
// focuses on tristate sync and bulk toggle.
export default class extends Controller {
  static targets = ["hsCheckbox"]
  static values = { url: String }

  connect() {
    this._updateHsCheckbox()
    requestAnimationFrame(() => this._dispatchPreChecked())
  }

  // HS-level checkbox changed — always uses system-level selection
  // so the Selected zone shows one group card, not individual location cards.
  toggle(event) {
    const checked = event.target.checked
    const sysId = this.element.id?.replace('hs_drilldown_', '')
    const sysName = this.element.dataset.hsName || ''
    const counter = sysId ? this._getCounterController() : null

    // Remove any individually-selected locations belonging to this system
    const locationCheckboxes = this._locationCheckboxes()
    locationCheckboxes.forEach(cb => {
      if (cb.checked) {
        const row = cb.closest('[data-add-provider-result-type]')
        if (row && counter) {
          const key = `location:${row.dataset.addProviderResultId}`
          counter.selections.delete(key)
        }
      }
      // Visually sync all location checkboxes with the HS checkbox
      cb.checked = checked
    })

    if (checked) {
      if (counter) counter.addSystemSelection(sysId, sysName)
    } else {
      if (counter) counter.removeSystemSelection(sysId)
    }
  }

  // Individual location checkbox changed — keep tristate in sync.
  // Counter is updated via its own data-action on each location checkbox.
  locationChanged() {
    this._updateHsCheckbox()
  }

  _updateHsCheckbox() {
    if (!this.hasHsCheckboxTarget) return
    const all = this._locationCheckboxes()
    if (all.length === 0) return

    const checkedCount = all.filter(cb => cb.checked).length
    const hsCheckbox = this.hsCheckboxTarget
    if (checkedCount === 0) {
      hsCheckbox.checked = false
      hsCheckbox.indeterminate = false
    } else if (checkedCount === all.length) {
      hsCheckbox.checked = true
      hsCheckbox.indeterminate = false
    } else {
      hsCheckbox.checked = false
      hsCheckbox.indeterminate = true
    }
  }

  // Dispatch change events for any pre-checked locations so the counter
  // controller picks them up on connect.
  _dispatchPreChecked() {
    // If the HS checkbox is checked, all locations are covered by the
    // system-level selection — skip individual dispatches.
    if (this.hasHsCheckboxTarget && this.hsCheckboxTarget.checked) return

    const all = this._locationCheckboxes()
    const checked = all.filter(cb => cb.checked)
    if (checked.length === 0) {
      this._refreshCounter()
      return
    }

    checked.forEach(cb => {
      cb.dispatchEvent(new Event("change", { bubbles: true }))
    })
  }

  _getCounterController() {
    const counterEl = document.querySelector('[data-controller~="add-provider-counter"]')
    if (!counterEl) return null
    return this.application.getControllerForElementAndIdentifier(counterEl, "add-provider-counter")
  }

  _refreshCounter() {
    const counter = this._getCounterController()
    if (counter) counter.refresh()
  }

  _fetchTurboStream(url) {
    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      }
    })
      .then(r => r.text())
      .then(html => { window.Turbo.renderStreamMessage(html) })
  }

  _locationCheckboxes() {
    return [...this.element.querySelectorAll('input[type="checkbox"][name="location_ids[]"]:not(:disabled)')]
  }
}
